// Product photo hover zoom, the way Gilly's old store did it: on a mouse, the photo magnifies inside its own frame
// and follows the cursor. Nothing is drawn outside the frame, so the info column and buy box are never covered.
// One set of delegated listeners on the document, so it survives Horizon swapping the gallery for a new scent.
// Touch and keyboard are untouched: a tap or Enter on a photo still opens Horizon's zoom dialog.
const SCALE = 2;
const TARGET_WIDTH = 2000;
const HOVER = window.matchMedia('(hover: hover) and (pointer: fine)');
const preloaded = new Map(); // url -> Promise<boolean>

let frame = null;
let lens = null;
let image = null;
let raf = 0;
let point = null;

/** Smallest srcset candidate of at least TARGET_WIDTH, else the largest one, else data_max_resolution, else the current source. */
function highResUrl(img) {
  let best = null;
  for (const part of (img.getAttribute('srcset') || '').split(',')) {
    const [url, descriptor] = part.trim().split(/\s+/);
    const w = parseInt(descriptor, 10);
    if (!url || !w) continue;
    if (!best || (best.w < TARGET_WIDTH ? w > best.w : w >= TARGET_WIDTH && w < best.w)) best = { url, w };
  }
  return best?.url || img.getAttribute('data_max_resolution') || img.currentSrc || img.src;
}

function preload(url) {
  if (!preloaded.has(url)) {
    preloaded.set(url, new Promise((resolve) => {
      const probe = new Image();
      probe.onload = () => resolve(true);
      probe.onerror = () => resolve(false);
      probe.src = url;
    }));
  }
  return preloaded.get(url);
}

function setBackground(url) {
  if (lens) lens.style.backgroundImage = `url("${url}")`;
}

function hide() {
  cancelAnimationFrame(raf);
  raf = 0;
  lens?.remove();
  frame = lens = image = point = null;
}

function show(nextFrame, img) {
  hide();
  if (!img.naturalWidth) return;
  frame = nextFrame;
  image = img;
  if (getComputedStyle(frame).position === 'static') frame.style.position = 'relative';
  lens = document.createElement('div');
  lens.className = 'gilly-zoom-lens';
  lens.setAttribute('aria-hidden', 'true');
  lens.style.backgroundSize = `${SCALE * 100}% ${SCALE * 100}%`;
  setBackground(img.currentSrc || img.src); // the picture is there straight away; the sharp copy replaces it when loaded
  frame.append(lens);
  const url = highResUrl(img);
  preload(url).then((ok) => {
    if (ok && lens && image === img) setBackground(url);
  });
}

/**
 * Place the lens over the part of the picture that is actually showing and aim it at the cursor. The image element's own
 * object-fit decides how the picture sits in its box: contain letterboxes it, cover crops it, fill stretches it.
 */
function update() {
  raf = 0;
  if (!lens || !frame || !image || !frame.isConnected || !point) return;
  const f = frame.getBoundingClientRect();
  const box = image.getBoundingClientRect();
  const fit = getComputedStyle(image).objectFit;
  const nw = image.naturalWidth;
  const nh = image.naturalHeight;
  let dw = box.width; // the picture's displayed size (it can be larger than the box when cropped)
  let dh = box.height;
  if (fit === 'contain' || fit === 'scale-down') {
    const k = Math.min(box.width / nw, box.height / nh);
    dw = nw * k;
    dh = nh * k;
  } else if (fit === 'cover') {
    const k = Math.max(box.width / nw, box.height / nh);
    dw = nw * k;
    dh = nh * k;
  }
  const w = Math.min(dw, box.width);
  const h = Math.min(dh, box.height);
  const left = box.left + (box.width - w) / 2;
  const top = box.top + (box.height - h) / 2;
  lens.style.left = `${left - f.left}px`;
  lens.style.top = `${top - f.top}px`;
  lens.style.width = `${w}px`;
  lens.style.height = `${h}px`;
  // the lens shows the whole picture at 2x; a cropped picture is magnified from its own (larger) displayed size
  lens.style.backgroundSize = Math.abs(dw - w) < 1 && Math.abs(dh - h) < 1 ? `${SCALE * 100}% ${SCALE * 100}%` : `${SCALE * dw}px ${SCALE * dh}px`;
  const x = Math.min(100, Math.max(0, ((point.x - left) / w) * 100));
  const y = Math.min(100, Math.max(0, ((point.y - top) / h) * 100));
  lens.style.backgroundPosition = `${x}% ${y}%`;
}

function frameFor(target) {
  if (!(target instanceof Element)) return null;
  const f = target.closest('media-gallery .product-media');
  if (!f || f.closest('zoom-dialog')) return null;
  if (!f.closest('.product-media-container--image')) return null;
  const img = f.querySelector('img.product-media__image');
  return img ? { f, img } : null;
}

document.addEventListener('pointermove', (event) => {
  if (event.pointerType !== 'mouse' || !HOVER.matches) return;
  const hit = frameFor(event.target);
  if (!hit) return lens ? hide() : undefined;
  if (hit.f !== frame || hit.img !== image) show(hit.f, hit.img);
  if (!lens) return;
  point = { x: event.clientX, y: event.clientY };
  if (!raf) raf = requestAnimationFrame(update);
}, { passive: true });

// leaving the window, opening the dialog, scrolling the page or paging the carousel all end the hover
document.documentElement.addEventListener('pointerleave', hide, { passive: true });
document.addEventListener('click', hide, { capture: true, passive: true });
document.addEventListener('scroll', () => lens && hide(), { capture: true, passive: true });
document.addEventListener('keydown', () => lens && hide(), { capture: true, passive: true });

// Keyboard: the carousel scroller is a focusable region (Phase D); Enter or Space opens the zoom dialog on the photo in view.
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Enter' && event.key !== ' ') return;
  if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;   // leave browser and assistive shortcuts alone
  const scroller = event.target;
  if (!(scroller instanceof Element) || !scroller.matches('media-gallery slideshow-slides')) return;
  const slide = scroller.querySelector('slideshow-slide:not([aria-hidden="true"])[on\\:click]');
  if (!slide) return;
  event.preventDefault();
  slide.click();
});
